import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, ExternalLink, FileSpreadsheet, RefreshCw, RotateCcw, UserX, Wallet } from 'lucide-react';
import jalaali from 'jalaali-js';
import PageHeader from '@/components/shared/PageHeader';
import LoadingLock from '@/components/shared/LoadingLock';
import PersianMonthPickerFa, { monthApiToDisplay } from '@/components/shared/PersianMonthPickerFa';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import SearchableSelect from '@/components/shared/SearchableSelect';
import PayrollFormulaBar, { type FormulaApplyScope } from '@/components/payroll/PayrollFormulaBar';
import PayrollSpreadsheetTable from '@/components/payroll/PayrollSpreadsheetTable';
import PayrollAccrualSettingsModal, {
  type PayrollAccrualModalSummary,
} from '@/components/payroll/PayrollAccrualSettingsModal';
import type { PayrollDocSettings } from '@/lib/payroll/payrollDocSettings';
import {
  accountingApi,
  type PayrollCalculatorEditableRow,
  type PayrollCalculatorProjectOption,
} from '@/lib/api/accounting';
import { formatRial } from '@/lib/formatMoney';
import { loadPayrollProjectOptions, normalizeMonthApi } from '@/lib/payroll/loadPayrollProjects';
import { getProjectManagerAppUrl } from '@/lib/projectManagerAppUrl';
import {
  applyPayrollFormulaToRows,
  buildEmployeePayrollSummary,
  filterActivePayrollRows,
  groupRowsByProject,
  patchEditableRow,
  savePayrollDraft,
  setPayrollEmployeeExcluded,
  syncEmployeeBankSelection,
  toEditableRows,
  type PayrollRowPatch,
} from '@/lib/payrollCalculator';
import { exportPayrollExcel } from '@/lib/exportPayrollExcel';

function currentJalaliMonth(): string {
  const now = new Date();
  const j = jalaali.toJalaali(now.getFullYear(), now.getMonth() + 1, now.getDate());
  return `${j.jy}-${String(j.jm).padStart(2, '0')}`;
}

const PayrollCalculatorPage: React.FC = () => {
  const navigate = useNavigate();
  const [month, setMonth] = React.useState(currentJalaliMonth);
  const [monthLabel, setMonthLabel] = React.useState('');
  const [projects, setProjects] = React.useState<PayrollCalculatorProjectOption[]>([]);
  const [projectsLoading, setProjectsLoading] = React.useState(false);
  const [projectsWarning, setProjectsWarning] = React.useState('');
  const [selectedProjectIds, setSelectedProjectIds] = React.useState<number[]>([]);
  const [projectSearch, setProjectSearch] = React.useState('');
  const [rows, setRows] = React.useState<PayrollCalculatorEditableRow[]>([]);
  const [meta, setMeta] = React.useState<{ month_label: string; date_range_label: string } | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [exportingExcel, setExportingExcel] = React.useState(false);
  const [accrualModalOpen, setAccrualModalOpen] = React.useState(false);

  const pmAttendanceUrl = getProjectManagerAppUrl('/attendance');

  const loadProjects = React.useCallback(async (monthValue: string) => {
    if (!monthValue.trim()) return;
    setProjectsLoading(true);
    setProjectsWarning('');
    try {
      const data = await loadPayrollProjectOptions(monthValue.trim());
      if (data.month !== monthValue.trim()) {
        setMonth(data.month);
      }
      setProjects(data.projects);
      setMonthLabel(data.month_label || monthApiToDisplay(data.month));
      setProjectsWarning(data.warning || '');
      setSelectedProjectIds((prev) => {
        const valid = new Set(data.projects.map((p) => p.id));
        const kept = prev.filter((id) => valid.has(id));
        if (kept.length > 0) return kept;
        return data.projects.filter((p) => p.has_attendance).map((p) => p.id);
      });
    } catch (e) {
      setProjects([]);
      setMonthLabel('');
      setProjectsWarning(e instanceof Error ? e.message : 'بارگذاری پروژه‌ها ناموفق بود.');
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadProjects(month);
  }, [month, loadProjects]);

  const filteredProjects = React.useMemo(() => {
    const q = projectSearch.trim();
    if (!q) return projects;
    return projects.filter((p) => p.label.includes(q));
  }, [projects, projectSearch]);

  const withAttendance = filteredProjects.filter((p) => p.has_attendance);
  const withoutAttendance = filteredProjects.filter((p) => !p.has_attendance);

  const projectGroups = React.useMemo(() => groupRowsByProject(rows), [rows]);
  const employeeSummary = React.useMemo(() => buildEmployeePayrollSummary(rows), [rows]);
  const formulaProjectOptions = React.useMemo(
    () => projectGroups.map((g) => ({ id: g.project_id, label: g.project_name })),
    [projectGroups],
  );

  const toggleProject = (id: number, checked: boolean) => {
    setSelectedProjectIds((prev) =>
      checked ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((x) => x !== id),
    );
  };

  const selectAllWithAttendance = () => {
    const ids = projects.filter((p) => p.has_attendance).map((p) => p.id);
    setSelectedProjectIds(ids);
  };

  const clearSelection = () => setSelectedProjectIds([]);

  const loadPreview = async () => {
    const normalizedMonth = normalizeMonthApi(month.trim());
    if (!normalizedMonth || selectedProjectIds.length === 0) {
      setError('ماه و حداقل یک پروژه را انتخاب کنید.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await accountingApi.payrollCalculatorPreview({
        month: normalizedMonth,
        project_id: selectedProjectIds,
      });
      setRows(toEditableRows(data.rows));
      setMeta({ month_label: data.month_label, date_range_label: data.date_range_label });
      if (data.warning) {
        setError(data.warning);
      }
    } catch (e) {
      setRows([]);
      setMeta(null);
      setError(e instanceof Error ? e.message : 'بارگذاری داده‌های حقوق ناموفق بود.');
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (rowKey: string, patch: PayrollRowPatch) => {
    setRows((prev) => {
      const target = prev.find((r) => r.row_key === rowKey);
      if (!target) return prev;
      let next = prev.map((r) => (r.row_key === rowKey ? patchEditableRow(r, patch) : r));
      if (patch.selected_bank_account_id !== undefined) {
        next = syncEmployeeBankSelection(next, target.employee_id, patch.selected_bank_account_id ?? null);
      }
      return next;
    });
  };

  const handleApplyFormula = (formula: string, scope: FormulaApplyScope) => {
    setRows((prev) =>
      applyPayrollFormulaToRows(
        prev,
        formula,
        scope === 'all' ? undefined : (row) => row.project_id === scope,
      ),
    );
  };

  const updateEmployeeBank = (employeeId: number, bankAccountId: number | null) => {
    setRows((prev) => syncEmployeeBankSelection(prev, employeeId, bankAccountId));
  };

  const activeRows = React.useMemo(() => filterActivePayrollRows(rows), [rows]);
  const excludedRows = React.useMemo(() => rows.filter((r) => r.excluded), [rows]);

  const totals = React.useMemo(() => {
    const totalNet = employeeSummary.reduce((s, e) => s + e.total_net, 0);
    return {
      totalNet,
      employees: employeeSummary.length,
      rowCount: activeRows.length,
      excludedCount: excludedRows.length,
      projectCount: projectGroups.length,
    };
  }, [employeeSummary, activeRows.length, excludedRows.length, projectGroups.length]);

  const excludeEmployee = (employeeId: number) => {
    setRows((prev) => setPayrollEmployeeExcluded(prev, employeeId, true));
  };

  const restoreEmployee = (employeeId: number) => {
    setRows((prev) => setPayrollEmployeeExcluded(prev, employeeId, false));
  };

  const accrualModalSummary = React.useMemo((): PayrollAccrualModalSummary | null => {
    if (!employeeSummary.length) return null;
    const creditTotal = employeeSummary.reduce((s, e) => s + e.total_net, 0);
    const debitTotal = activeRows.reduce((s, r) => s + r.computed_net, 0);
    return {
      monthLabel: meta?.month_label || monthLabel || month,
      dateRangeLabel: meta?.date_range_label,
      employeeCount: employeeSummary.length,
      projectDebitLines: new Set(activeRows.map((r) => r.project_id)).size,
      totalNet: totals.totalNet,
      creditTotal,
      debitTotal,
    };
  }, [employeeSummary, activeRows, meta, monthLabel, month, totals.totalNet]);

  const persistPayrollDraft = React.useCallback(() => {
    savePayrollDraft({
      month,
      month_label: meta?.month_label,
      date_range_label: meta?.date_range_label,
      project_ids: selectedProjectIds,
      rows: employeeSummary.map((e) => ({
        employee_id: e.employee_id,
        employee_name: e.employee_name,
        project_id: e.project_parts[0]?.project_id ?? 0,
        project_name: e.project_parts.map((p) => p.project_name).join('، '),
        net_salary: e.total_net,
        bank_name: e.bank_name,
        account_number: e.account_number,
        iban: e.iban,
        project_breakdown: e.project_parts,
      })),
      project_rows: activeRows.map((r) => ({
        employee_id: r.employee_id,
        employee_name: r.employee_name,
        project_id: r.project_id,
        project_name: r.project_name,
        net_salary: r.computed_net,
      })),
      detail_rows: activeRows,
      total_net: totals.totalNet,
      created_at: new Date().toISOString(),
      note: 'استقرار حقوق — ابتدا بستانکار بدهی پرسنل، سپس بدهکار هزینه پروژه',
    });
  }, [month, meta, selectedProjectIds, employeeSummary, activeRows, totals.totalNet]);

  const openAccrualModal = () => {
    if (!employeeSummary.length) return;
    setAccrualModalOpen(true);
  };

  const confirmAccrualDocument = (_settings: PayrollDocSettings) => {
    persistPayrollDraft();
    navigate('/accounting-documents/new?fromPayroll=1');
  };

  const exportExcel = async () => {
    if (!employeeSummary.length) return;
    setExportingExcel(true);
    try {
      await exportPayrollExcel({
        month,
        month_label: meta?.month_label || monthLabel || month,
        date_range_label: meta?.date_range_label,
        projectGroups,
        employeeSummary,
        detailRows: activeRows,
        totalNet: totals.totalNet,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'خروجی اکسل ناموفق بود.');
    } finally {
      setExportingExcel(false);
    }
  };

  const renderProjectRow = (p: PayrollCalculatorProjectOption) => {
    const checked = selectedProjectIds.includes(p.id);
    return (
      <label
        key={p.id}
        className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition hover:bg-slate-50 ${
          checked ? 'border-indigo-200 bg-indigo-50/50' : 'border-slate-200 bg-white'
        }`}
      >
        <Checkbox
          checked={checked}
          onCheckedChange={(v) => toggleProject(p.id, v === true)}
          className="mt-0.5"
        />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-slate-800">{p.label}</div>
          <div className="mt-0.5 flex flex-wrap gap-2 text-xs text-slate-500">
            {p.has_attendance ? (
              <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-800">
                {p.attendance_count} رکورد حضور و غیاب
              </span>
            ) : (
              <span className="rounded bg-slate-100 px-1.5 py-0.5">بدون حضور در این ماه</span>
            )}
            {p.personnel_count > 0 ? <span>{p.personnel_count} نفر پرسنل</span> : null}
          </div>
        </div>
      </label>
    );
  };

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title="محاسبه حقوق و دستمزد"
        description="خواندن از حضور و غیاب و پرسنل پروژه — فقط محاسبه در مرورگر؛ هیچ رکوردی در دیتابیس ذخیره نمی‌شود."
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Calculator className="h-5 w-5 text-indigo-600" />
            فیلتر محاسبه
          </CardTitle>
          <CardDescription>ماه را از تقویم انتخاب کنید، پروژه‌ها را تیک بزنید و بارگذاری کنید.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 md:grid-cols-[14rem_1fr]">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">ماه محاسبه</label>
              <PersianMonthPickerFa value={month} onChange={setMonth} placeholder="انتخاب ماه…" />
              {monthLabel ? <p className="mt-1 text-xs text-slate-500">{monthLabel}</p> : null}
            </div>
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="text-xs font-medium text-slate-600">انتخاب پروژه‌ها</label>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={selectAllWithAttendance}>
                    انتخاب همه با حضور و غیاب
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={clearSelection}>
                    پاک کردن انتخاب
                  </Button>
                  <Button type="button" variant="outline" size="sm" asChild>
                    <a href={pmAttendanceUrl} target="_blank" rel="noopener noreferrer" className="gap-1.5">
                      <ExternalLink className="h-3.5 w-3.5" />
                      ثبت حضور در مدیریت پروژه
                    </a>
                  </Button>
                </div>
              </div>
              <Input
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                placeholder="جستجو در نام پروژه…"
                className="h-9"
              />
              <div className="max-h-72 space-y-3 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                {projectsLoading ? (
                  <p className="py-4 text-center text-sm text-slate-500">در حال بارگذاری پروژه‌ها…</p>
                ) : !filteredProjects.length ? (
                  <div className="space-y-3 py-4 text-center text-sm text-slate-500">
                    <p>پروژه‌ای یافت نشد.</p>
                    <Button type="button" variant="outline" size="sm" asChild>
                      <a href={pmAttendanceUrl} target="_blank" rel="noopener noreferrer" className="gap-1.5">
                        <ExternalLink className="h-3.5 w-3.5" />
                        باز کردن پنل حضور و غیاب
                      </a>
                    </Button>
                  </div>
                ) : (
                  <>
                    {withAttendance.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-emerald-800">پروژه‌های دارای حضور و غیاب</p>
                        {withAttendance.map(renderProjectRow)}
                      </div>
                    ) : null}
                    {withoutAttendance.length > 0 ? (
                      <div className="space-y-2">
                        {withAttendance.length > 0 ? (
                          <p className="pt-1 text-xs font-semibold text-slate-500">سایر پروژه‌ها</p>
                        ) : (
                          <p className="text-xs font-semibold text-amber-800">
                            پروژه‌ها بدون رکورد حضور در این ماه — برای ثبت حضور به مدیریت پروژه بروید.
                          </p>
                        )}
                        {withoutAttendance.map(renderProjectRow)}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
              <p className="text-xs text-slate-500">
                {selectedProjectIds.length} پروژه انتخاب شده
                {projects.length > 0 ? ` · ${projects.length} پروژه در لیست` : ''}
              </p>
            </div>
          </div>

          {projectsWarning ? (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {projectsWarning}
            </p>
          ) : null}

          {error ? (
            <p
              className={`rounded-md border px-3 py-2 text-sm ${
                rows.length > 0
                  ? 'border-amber-200 bg-amber-50 text-amber-900'
                  : 'border-rose-200 bg-rose-50 text-rose-700'
              }`}
            >
              {error}
            </p>
          ) : null}

          <Button type="button" className="gap-2" onClick={loadPreview} disabled={loading || projectsLoading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            بارگذاری از حضور و غیاب
          </Button>
        </CardContent>
      </Card>

      <LoadingLock loading={loading}>
        {meta && rows.length > 0 ? (
          <>
            <Card className="border-indigo-200 bg-indigo-50/30">
              <CardHeader className="flex flex-col gap-2 border-b border-indigo-100 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="text-base">خلاصه کل — {meta.month_label}</CardTitle>
                  <CardDescription>{meta.date_range_label}</CardDescription>
                </div>
                <div className="flex flex-wrap gap-2 text-sm">
                  <span className="rounded-lg bg-white px-3 py-1">{totals.projectCount} پروژه</span>
                  <span className="rounded-lg bg-white px-3 py-1">{totals.employees} نفر (یکتا)</span>
                  <span className="rounded-lg bg-white px-3 py-1">{totals.rowCount} ردیف فعال</span>
                  {totals.excludedCount > 0 ? (
                    <span className="rounded-lg bg-slate-200 px-3 py-1 text-slate-700">
                      {totals.excludedCount} ردیف خارج از محاسبه
                    </span>
                  ) : null}
                  <span className="rounded-lg bg-emerald-100 px-3 py-1 font-mono font-semibold text-emerald-900">
                    جمع خالص پرداخت: {formatRial(totals.totalNet)}
                  </span>
                </div>
              </CardHeader>
            </Card>

            <p className="text-xs text-slate-600">
              برای حذف از محاسبه، اکسل و سند: روی آیکون{' '}
              <UserX className="inline h-3.5 w-3.5 text-slate-500" /> در هر ردیف کلیک کنید، یا در جدول پرسنل «خارج
              کردن» را بزنید. ردیف‌های حذف‌شده با خط خورده نمایش داده می‌شوند و قابل بازگردانی هستند.
            </p>

            <PayrollFormulaBar
              projectOptions={formulaProjectOptions}
              rows={activeRows}
              onApply={handleApplyFormula}
            />

            <div className="space-y-4">
              {projectGroups.map((group, index) => (
                <Card key={group.project_id}>
                  <CardHeader className="flex flex-col gap-2 border-b sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500">بخش {index + 1} از {projectGroups.length}</p>
                      <CardTitle className="text-base">{group.project_name}</CardTitle>
                      <CardDescription>
                        {group.employeeCount} نفر فعال · {group.rows.filter((r) => !r.excluded).length} از{' '}
                        {group.rows.length} ردیف در محاسبه
                      </CardDescription>
                    </div>
                    <span className="rounded-lg bg-emerald-50 px-3 py-1.5 font-mono text-sm font-semibold text-emerald-800">
                      جمع پروژه: {formatRial(group.totalNet)}
                    </span>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    <PayrollSpreadsheetTable rows={group.rows} onUpdateRow={updateRow} />
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="border-slate-300">
              <CardHeader className="flex flex-col gap-2 border-b sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="text-base">جمع‌بندی پرسنل (کل ماه)</CardTitle>
                  <CardDescription>
                    اگر یک نفر در چند پروژه کار کرده، خالص پرداخت اینجا تجمیع می‌شود — مبنای ثبت پرداخت.
                  </CardDescription>
                </div>
                <span className="rounded-lg bg-emerald-50 px-3 py-1.5 font-mono text-sm font-semibold text-emerald-800">
                  جمع کل: {formatRial(totals.totalNet)}
                </span>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-sm">
                    <thead>
                      <tr className="bg-slate-100 text-slate-700">
                        <th className="border px-2 py-2 text-center w-28">عملیات</th>
                        <th className="border px-2 py-2 text-right">پرسنل</th>
                        <th className="border px-2 py-2 text-right">پروژه‌ها</th>
                        <th className="border px-2 py-2 text-right">خالص تجمیعی</th>
                        <th className="border px-2 py-2 text-right">بانک</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeeSummary.map((e) => (
                        <tr key={e.employee_id} className="odd:bg-white even:bg-slate-50/60">
                          <td className="border px-2 py-2 text-center">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 gap-1 text-xs text-rose-700 hover:bg-rose-50"
                              onClick={() => excludeEmployee(e.employee_id)}
                            >
                              <UserX className="h-3.5 w-3.5" />
                              خارج کردن
                            </Button>
                          </td>
                          <td className="border px-2 py-2">
                            <div className="font-medium">{e.employee_name}</div>
                            {e.employee_code ? (
                              <div className="font-mono text-xs text-slate-500" dir="ltr">
                                {e.employee_code}
                              </div>
                            ) : null}
                          </td>
                          <td className="border px-2 py-2 text-xs text-slate-600">
                            {e.project_parts.map((p) => (
                              <div key={`${e.employee_id}-${p.project_id}`} className="flex flex-wrap items-center gap-1 py-0.5">
                                <span>{p.project_name}</span>
                                <span className="font-mono text-emerald-800">({formatRial(p.net)})</span>
                              </div>
                            ))}
                          </td>
                          <td className="border px-2 py-2 font-mono font-semibold text-emerald-800">
                            {formatRial(e.total_net)}
                          </td>
                          <td className="border px-2 py-2 text-xs text-slate-600">
                            {e.bank_accounts.length > 1 ? (
                              <SearchableSelect
                                value={e.selected_bank_account_id ? String(e.selected_bank_account_id) : ''}
                                onChange={(v) => updateEmployeeBank(e.employee_id, v ? Number(v) : null)}
                                options={e.bank_accounts.map((b) => ({
                                  value: String(b.id),
                                  label: b.label,
                                }))}
                                placeholder="انتخاب بانک"
                                className="h-8 text-xs"
                              />
                            ) : (
                              <>
                                {e.bank_name || '—'}
                                {e.iban ? (
                                  <div dir="ltr" className="font-mono">
                                    {e.iban}
                                  </div>
                                ) : null}
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {excludedRows.length > 0 ? (
              <Card className="border-slate-300 bg-slate-50/50">
                <CardHeader>
                  <CardTitle className="text-base text-slate-700">
                    خارج از محاسبه ({excludedRows.length} ردیف)
                  </CardTitle>
                  <CardDescription>
                    این ردیف‌ها در جمع، اکسل و سند استقرار حقوق لحاظ نمی‌شوند.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {excludedRows.map((r) => (
                    <div
                      key={r.row_key}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                    >
                      <div className="min-w-0">
                        <span className="font-medium text-slate-600 line-through">{r.employee_name}</span>
                        <span className="mx-2 text-slate-400">·</span>
                        <span className="text-slate-500">{r.project_name}</span>
                        <span className="ms-2 font-mono text-xs text-slate-400">{formatRial(r.computed_net)}</span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 gap-1 shrink-0 text-xs"
                        onClick={() => updateRow(r.row_key, { excluded: false })}
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        بازگرداندن
                      </Button>
                    </div>
                  ))}
                  {(() => {
                    const excludedEmployeeIds = [...new Set(excludedRows.map((r) => r.employee_id))];
                    const canRestoreWhole = excludedEmployeeIds.filter((empId) =>
                      rows.every((r) => r.employee_id !== empId || r.excluded),
                    );
                    if (canRestoreWhole.length === 0) return null;
                    return (
                      <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
                        {canRestoreWhole.map((empId) => {
                          const name = excludedRows.find((r) => r.employee_id === empId)?.employee_name ?? '';
                          return (
                            <Button
                              key={empId}
                              type="button"
                              variant="secondary"
                              size="sm"
                              className="h-8 gap-1 text-xs"
                              onClick={() => restoreEmployee(empId)}
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                              بازگرداندن همه ردیف‌های {name}
                            </Button>
                          );
                        })}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            ) : null}
          </>
        ) : meta ? (
          <Card>
            <CardContent className="py-10 text-center text-sm text-slate-500">
              پرسنلی برای پروژه‌های انتخاب‌شده در این ماه یافت نشد.
            </CardContent>
          </Card>
        ) : null}

        {employeeSummary.length > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-950">
            <p>
              محاسبه به تفکیک پروژه و تجمیع پرسنل انجام شده. ابتدا سند <strong>استقرار</strong> (بستانکار بدهی حقوق،
              بدهکار هزینه) ثبت کنید؛ پس از واریز، سند پرداخت جدا (بدهکار بدهی / بستانکار بانک) مانده را صفر می‌کند.
            </p>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                className="gap-2 bg-white"
                disabled={exportingExcel}
                onClick={() => void exportExcel()}
              >
                <FileSpreadsheet className="h-4 w-4" />
                {exportingExcel ? 'در حال ساخت…' : 'خروجی Excel'}
              </Button>
              <Button type="button" className="gap-2" onClick={openAccrualModal}>
                <Wallet className="h-4 w-4" />
                سند استقرار حقوق
              </Button>
            </div>
          </div>
        ) : null}
      </LoadingLock>

      <PayrollAccrualSettingsModal
        open={accrualModalOpen}
        onOpenChange={setAccrualModalOpen}
        summary={accrualModalSummary}
        onConfirm={confirmAccrualDocument}
      />
    </div>
  );
};

export default PayrollCalculatorPage;
